import type { ProductDto } from '@deli/contracts';
import { useEffect, useMemo } from 'react';
import { useAddImages, useRemoveImage, useReorderImages, useSetHero } from '../api/catalogue.js';
import { ErrorText } from '../components/ErrorText.js';
import { t } from '../i18n/uk.js';

const tc = t.catalogue;
const ACCEPT = 'image/jpeg,image/png,image/webp';

const filesOf = (input: HTMLInputElement) => {
  const files = [...(input.files ?? [])];
  input.value = ''; // choosing the same file again still triggers a change
  return files;
};

/**
 * The product's hero image and its extra images (catalogue.edit): replace or remove the hero,
 * add extras (several at once), move them left / right, remove them.
 */
export function ProductImages({ product }: { product: ProductDto }) {
  const setHero = useSetHero();
  const add = useAddImages();
  const remove = useRemoveImage();
  const reorder = useReorderImages();
  const busy = setHero.isPending || add.isPending || remove.isPending || reorder.isPending;
  const extras = product.images;

  const move = (index: number, by: -1 | 1) => {
    const ids = extras.map((i) => i.id);
    [ids[index], ids[index + by]] = [ids[index + by]!, ids[index]!];
    reorder.mutate({ productId: product.id, imageIds: ids });
  };

  return (
    <div className="images">
      <h2>{tc.images}</h2>
      <p className="muted hint">{tc.imageHint}</p>
      <div className="image-row">
        <figure className="hero">
          {product.heroImage ? (
            <img src={product.heroImage.url} alt={`${tc.heroImage}: ${product.name}`} />
          ) : product.wooImages[0] ? (
            <img
              src={product.wooImages[0]}
              alt={`${tc.storeImage}: ${product.name}`}
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="placeholder">{tc.noHero}</div>
          )}
          <figcaption>
            {tc.heroImage}
            <label className="file-button">
              {product.heroImage ? tc.replaceHero : tc.uploadHero}
              <input
                type="file"
                accept={ACCEPT}
                aria-label={tc.heroImage}
                disabled={busy}
                onChange={(e) => {
                  const [file] = filesOf(e.target);
                  if (file) setHero.mutate({ productId: product.id, file });
                }}
              />
            </label>
            {product.heroImage && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  remove.mutate({ productId: product.id, imageId: product.heroImage!.id })
                }
              >
                {t.common.remove}
              </button>
            )}
          </figcaption>
        </figure>
        <div className="extras">
          <div className="muted">{tc.extraImages}</div>
          <ul className="thumbs">
            {extras.map((image, index) => (
              <li key={image.id}>
                <img src={image.url} alt={`${tc.extraImages} ${index + 1}`} />
                <div className="thumb-actions">
                  <button
                    type="button"
                    className="icon"
                    aria-label={`${tc.moveLeft}: ${index + 1}`}
                    disabled={busy || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    className="icon"
                    aria-label={`${tc.moveRight}: ${index + 1}`}
                    disabled={busy || index === extras.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    →
                  </button>
                  <button
                    type="button"
                    className="icon"
                    aria-label={`${t.common.remove}: ${index + 1}`}
                    disabled={busy}
                    onClick={() => remove.mutate({ productId: product.id, imageId: image.id })}
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <label className="file-button">
            {tc.addImages}
            <input
              type="file"
              accept={ACCEPT}
              multiple
              aria-label={tc.addImages}
              disabled={busy}
              onChange={(e) => {
                const files = filesOf(e.target);
                if (files.length > 0) add.mutate({ productId: product.id, files });
              }}
            />
          </label>
        </div>
      </div>
      <ErrorText error={setHero.error ?? add.error ?? remove.error ?? reorder.error} />
    </div>
  );
}

/** Previews of files chosen before the product exists (object URLs, released on change). */
export function FilePreviews({ files, label }: { files: File[]; label: string }) {
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  if (files.length === 0) return null;
  return (
    <ul className="thumbs">
      {urls.map((url, i) => (
        <li key={url}>
          <img src={url} alt={`${label} ${i + 1}`} />
        </li>
      ))}
    </ul>
  );
}
